import { useParams } from "@tanstack/react-router";
import { Autocomplete, useFilter } from "react-aria-components";
import { Controller } from "react-hook-form";

import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Input } from "@/components/field";
import { ChevronDownIcon } from "@/components/icons.tsx";
import { SearchField } from "@/components/search-field";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectListItemDescription,
  SelectListItemLabel,
  SelectPopover,
} from "@/components/select";
import { Text } from "@/components/text";
import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useFieldUpdate } from "@/features/request/edit/hooks/useFieldUpdate";
import { useGetAssignableDesigners } from "@/features/request/edit/hooks/useGetAssignableDesigners";

export function SelectAssignee() {
  const { control, watch } = useEditRequestFormContext();
  const { requestId } = useParams({
    from: "/_auth/_app-shell/requests/$requestId/edit",
  });
  const { data: designers } = useGetAssignableDesigners(Number(requestId));
  const { updateField } = useFieldUpdate();
  const { contains } = useFilter({ sensitivity: "base" });
  const isComplexityMissing = !watch("complexityId");
  const currentAssignee = watch("assignee");

  return (
    <Controller
      control={control}
      name="designerId"
      render={({ field: { name, onChange, value } }) => {
        const selectedDesigner = designers?.find((d) => d.id === value);
        const selectedLabel =
          selectedDesigner?.textValue ??
          (currentAssignee && currentAssignee.id === value
            ? currentAssignee.name
            : undefined) ??
          "Select assignee";

        return (
          <Select
            aria-label="Select assignee"
            isDisabled={isComplexityMissing}
            name={name}
            onSelectionChange={(selectedValue) => {
              if (!selectedValue) return;
              onChange(selectedValue);
              void updateField(name, selectedValue);
            }}
            selectedKey={value}
          >
            <Button
              className="flex items-center justify-center outline-none whitespace-nowrap"
              variant="unstyled"
            >
              <Badge size="small" variant="primary">
                {selectedLabel}
                <Badge.Icon>
                  <ChevronDownIcon />
                </Badge.Icon>
              </Badge>
            </Button>

            <SelectPopover
              className="flex w-[250px] flex-col text-ellipsis overflow-hidden whitespace-nowrap"
              placement="bottom start"
            >
              <Autocomplete filter={contains}>
                <SearchField
                  aria-label="Search assignees"
                  autoFocus
                  className="shrink-0 px-sm pt-xs"
                >
                  <Input placeholder="Search assignees..." />
                </SearchField>

                <SelectListBox
                  className="min-h-0"
                  renderEmptyState={() => (
                    <Text className="p-sm text-center text-text-tertiary">
                      No assignees found
                    </Text>
                  )}
                >
                  {designers?.map((item) => (
                    <SelectListItem
                      id={item.id}
                      key={item.id}
                      textValue={item.textValue}
                    >
                      <SelectListItemLabel className="block truncate">
                        {item.textValue}
                      </SelectListItemLabel>

                      <SelectListItemDescription className="block w-full">
                        {item.designation}
                      </SelectListItemDescription>
                    </SelectListItem>
                  ))}
                </SelectListBox>
              </Autocomplete>
            </SelectPopover>
          </Select>
        );
      }}
    />
  );
}
